<template>
	<div class="form-group">
		<!--LABEL FOR INPUT-->
		<slot name="formFieldLabel">
			<label v-if="showLabel" class="form-group__label"
						 :for="field.name"
						 :class="{'is-required': fieldRequired}">{{ field.label }}
			</label>
		</slot>

		<!--Autocomplete-->
		<template v-if="field.type === TYPE_FIELD.AUTOCOMPLETE">
			<com-autocomplete v-model="inputValue"
												:input-class-list="{
                    'form-control': true,
                    'form-group__control': true,
                    'is-editable': field.editable,
                    'is-invalid': !fieldIsValid,
                    'is-required': fieldRequired,
                    'form-group__control--textarea': true
                }"
												:required="fieldRequired"
												:async-find="field.asyncFind"
												:readonly="field.readonly === true"
												:options="field.options"
												:vue-select-label="field.vueSelectLabel"
												:vue-select-track-by="field.vueSelectTrackBy"
												:id="field.name"
												:no-options="field.noOptionsLabel"
												:no-options-empty-search="field.noOptionsEmptySearchLabel"
												:no-result="field.noResultsLabel"
												:multiple="field.multiple"
												:placeholder="field.placeholder"
												:disabled="field.disabled === true">
			</com-autocomplete>
		</template>

		<template v-if="field.type === TYPE_FIELD.DATE_2">
			<com-datepicker2
				v-model="inputValue"
				v-bind="datepicker2Props"
			>
				<slot name="icon-calendar" />
			</com-datepicker2>
		</template>

		<!-- Date-->
		<template v-if="field.type === TYPE_FIELD.DATE">
			<com-datetime-input
				v-model="inputValue"
				:input-class-list="{
                    'form-group__control': true,
                    'form-group__control--datetime': true,
                    'form-control': true,
                    'form-control-sm': true,
                    'is-editable': field.editable,
                    'is-invalid': !fieldIsValid,
                    'is-required': fieldRequired
                }"
				:picker-type="field.pickerType"
				:date-format="field.dateFormat"
				:input-format="field.inputFormat"
				:output-format="field.outputFormat"
				:required="fieldRequired"
				:readonly="field.readonly"
				:disabled="!field.editable"
				:use-icon="field.useIcon"
				:id="field.name"
				:name="field.name"
				:placeholder="field.placeholder"
				:append-to-body="field.appendToBody"
				:confirm="field.confirm"
				@blur="formInputValueChangeHandler($event)"
			>
				<template #icon>
					<slot name="icon"></slot>
				</template>
			</com-datetime-input>
		</template>

		<!--Checkbox-->
		<template v-if="field.type === TYPE_FIELD.CHECKBOX">
			<label class="form-group__label"
						 :for="field.name"
						 :class="{'is-required': fieldRequired}">
				<com-checkbox
					v-model="inputValue"
					:input-class-list="{
							'form-group__control': true,
							'form-group__control--checkbox': true,
							'is-editable': field.editable,
							'is-invalid': !fieldIsValid,
              'is-required': fieldRequired
						}"
					:required="fieldRequired"
					:readonly="field.readonly === true"
					:disabled="field.disabled === true"
					:id="field.name"
					:name="field.name"
				/>
				<span v-html="field.label"></span>
			</label>
		</template>

		<!--Text-->
		<template v-else-if="field.type === TYPE_FIELD.TEXT || field.type === TYPE_FIELD.PASSWORD">
			<com-text-input
				v-model="inputValue"
				:input-class-list="{
							'form-group__control': true,
							'form-group__control--text': true,
							'form-control form-control-sm': true,
							'is-editable': field.editable,
							'is-invalid': !fieldIsValid,
              'is-required': fieldRequired
						}"
				:required="fieldRequired"
				:readonly="field.readonly === true"
				:id="field.name"
				:type="field.inputType ? field.inputType : 'text'"
				:placeholder="field.placeholder"
				:disabled="field.disabled === true"
				:show-copy-to-clipboard="field.showCopyToClipboard || false"
				@blur="onBlurHandler"
			/>
		</template>

		<!--Text-->
		<template v-else-if="field.type === TYPE_FIELD.RADIO">
			<com-radio-input
				v-model="inputValue"
				:input-class-list="{
							'form-group__control': true,
							'form-group__control--text': true,
							'form-control form-control-sm': true,
							'is-editable': field.editable,
							'is-invalid': !fieldIsValid,
              'is-required': fieldRequired
						}"
				:required="fieldRequired"
				:readonly="field.readonly === true"
				:name="field.name"
				:options="field.options"
				:placeholder="field.placeholder"
				:disabled="field.disabled === true"
			>
				<template #icon="{option}">
					<slot name="icon" :option="option"></slot>
				</template>
			</com-radio-input>
		</template>

		<!--HighlightableInput-->
		<template v-else-if="field.type === TYPE_FIELD.HIGHLIGHTABLE_INPUT">
			<com-highlightable-input
				v-model="inputValue"
				:input-class-list="{
							'form-group__control': true,
							'form-group__control--text': true,
							'form-control form-control-sm': true,
							'is-editable': field.editable,
							'is-invalid': !fieldIsValid,
              'is-required': fieldRequired
						}"
				:required="fieldRequired"
				:highlightEnabled="field.highlightEnabled"
				:highlight="field.highlight"
				:readonly="field.readonly === true"
				:id="field.name"
				:placeholder="field.placeholder"
				:disabled="field.disabled === true"
			/>
		</template>

		<!--Select-->
		<template v-else-if="field.type === TYPE_FIELD.SELECT">
			<com-select-input
				v-model="inputValue"
				:input-class-list="{
							'form-group__control': true,
							'form-group__control--text': true,
							'form-control form-control-sm': true,
							'is-editable': field.editable,
							'is-invalid': !fieldIsValid,
              'is-required': fieldRequired
						}"
				:required="fieldRequired"
				:multiple="field.multiple"
				:readonly="field.readonly === true"
				:id="field.name"
				:placeholder="field.placeholder"
				:disabled="field.disabled === true"
				:options="field.options"
				:optionTrackBy="field.optionTrackBy"
				:optionLabel="field.optionLabel"
			/>
		</template>

		<!--Select Object-->
		<template v-else-if="field.type === TYPE_FIELD.SELECT_OBJECT">
			<com-select-object-input
				v-model="inputValue"
				:input-class-list="{
							'form-group__control': true,
							'single-value': true,
							'form-group__control--text': true,
							'form-control form-control-sm': true,
							'is-editable': field.editable,
							'is-invalid': !fieldIsValid,
              'is-required': fieldRequired
						}"
				:required="fieldRequired"
				:readonly="field.readonly === true"
				:id="field.name"
				:placeholder="field.placeholder"
				:disabled="field.disabled === true"
				:options="field.options"
				:optionTrackBy="field.optionTrackBy"
				:optionLabel="field.optionLabel"
			/>
		</template>

		<!--Number-->
		<template v-else-if="field.type === TYPE_FIELD.NUMBER">
			<com-number-input
				v-model="inputValue"
				:input-class-list="{
							'form-group__control': true,
							'form-group__control--number': true,
							'form-control form-control-sm': true,
							'is-editable': field.editable,
							'is-invalid': !fieldIsValid,
              'is-required': fieldRequired
						}"
				:required="fieldRequired"
				:readonly="field.readonly === true"
				:id="field.name"
				:placeholder="field.placeholder"
				:disabled="field.disabled === true"
				@blur="onBlurHandler"
			/>
		</template>

		<!--Textarea-->
		<template v-else-if="field.type === TYPE_FIELD.TEXTAREA">
			<com-textarea
				v-model="inputValue"
				:input-class-list="{
							'form-control': true,
							'form-group__control': true,
							'is-editable': field.editable,
							'is-invalid': !fieldIsValid,
              'is-required': fieldRequired,
							'form-group__control--textarea': true
						}"
				:required="fieldRequired"
				:readonly="field.readonly === true"
				:id="field.name"
				:rows="field.rows"
				:cols="field.cols"
				:placeholder="field.placeholder"
				:disabled="field.disabled === true"
				@blur="onBlurHandler"
			/>
		</template>

		<!--Multiselect-->
		<template v-else-if="field.type === TYPE_FIELD.MULTISELECT">
			<com-multi-select
				v-model="inputValue"
				:input-class-list="{
							'form-control': true,
							'form-group__control': true,
							'is-editable': field.editable,
							'is-invalid': !fieldIsValid,
              'is-required': fieldRequired,
							'form-group__control--textarea': true
						}"
				:required="fieldRequired"
				:readonly="field.readonly === true"
				:options="field.options"
				:taggable="field.taggable"
				:max="field.vueSelectMax"
				:vue-select-label="field.vueSelectLabel"
				:vue-select-track-by="field.vueSelectTrackBy"
				:minSelectionShowCount="field.minSelectionShowCount"
				:id="field.name"
				:placeholder="field.placeholder"
				:disabled="field.disabled === true"
				:async-find="field.asyncFind"
			/>
		</template>

		<!--PlainText-->
		<template v-else-if="field.type === TYPE_FIELD.PLAIN">
			<com-plain-text
				:value="field.getFormFormattedValue()"
				:input-class-list="{}"
				:id="field.name"
				:name="field.name"
			/>
		</template>

		<slot name="formFieldValidations">
			<div class="form-group__details">
				<div v-for="message in [...fieldMessages, ...messages]" class="form-group__messages" role="alert">
					{{ message }}
				</div>
			</div>
		</slot>

	</div>
</template>

<script>
import comAutocomplete                                           from './elements/Autocomplete/App.vue';
import comDatetimeInput                                          from './elements/DatetimeInput/App.vue';
import comDatepicker2, { defineProps as defineDatepicker2Props } from './elements/Datepicker';
import comHighlightableInput                                     from './elements/HighlightableInput/App.vue';
import comTextInput                                              from './elements/TextInput/App.vue';
import comNumberInput                                            from './elements/NumberInput/App.vue';
import comCheckbox                                               from './elements/CheckboxInput/App.vue';
import comTextarea                                               from './elements/Textarea/App.vue';
import comPlainText                                              from './elements/PlainText/App.vue';
import comSelectInput                                            from './elements/SelectInput/App.vue';
import comRadioInput                                             from './elements/RadioInput/App.vue';
import comSelectObjectInput                                      from './elements/SelectObjectInput/App.vue';
import comMultiSelect                                            from './elements/MultiSelectInput/App.vue';

import TYPE_FIELD from './field_types';

export default {
	name: 'FormField',

	data() {
		return {
			TYPE_FIELD,
			fieldMessages: [],
			valid: true,
			validForSave: true,
			initialized: true
		};
	},

	props: {
		schema: {
			type: Object,
			required: true
		},
		fieldName: {
			type: String,
			required: true
		},
		formData: {
			type: Object
		},
		value: {
			type: [String, Number, Object, Array, Boolean, Date]
		},
		messages: {
			type: Array,
			default: () => []
		}
	},

	components: {
		comAutocomplete,
		comDatetimeInput,
		comCheckbox,
		comTextInput,
		comSelectInput,
		comSelectObjectInput,
		comNumberInput,
		comTextarea,
		comPlainText,
		comHighlightableInput,
		comMultiSelect,
		comRadioInput,
		comDatepicker2
	},

	mounted() {
		this.validate();
	},

	methods: {
		validate() {
			this.isFieldValid(this.field, this.value);
		},
		formInputValueChangeHandler(value) {
			this.$emit('input', value || this.defaultValue);
			this.isFieldValid(this.field, value || this.defaultValue);
		},
		isFieldValid(field, value) {
			this.fieldMessages = [];

			if ( this.activeValidation && field.validators && field.validators.length > 0 ) {

				field.validators.forEach(validator => {

					if ( validator instanceof Function ) {
						let v = validator(value);
						if ( v.valid === false ) {
							this.fieldMessages.push(this.$t(v.message.text, v.message.params));
						}
					} else {
						this.fieldMessages.push(this.$t('vforms.validator_not_found'));
					}
				});

				field.valid = this.fieldMessages.length < 1;
			} else {
				field.valid = true;
			}
		},
		onBlurHandler(event) {
			this.$emit("blur", this.inputValue)
		},
	},

	computed: {
		/**
		 * @returns {Record<string,*>}
		 */
		datepicker2Props() {
			if ( this.field.type !== TYPE_FIELD.DATE_2 ) {
				return null;
			}

			return defineDatepicker2Props({
				disabled: !this.field.editable,
				editable: this.field.readonly,
				appendToBody: this.field.appendToBody,
				placeholder: this.field.placeholder,
				inputClass: [
					'form__control',
					'form__control-sm',
					'form-group__control',
					'form-group__control--datetime',
					'is-editable' ? this.field.editable : null,
					'is-invalid' ? this.fieldIsValid : null,
					'is-required' ? this.fieldRequired : null
				].filter(Boolean).join(' '),
				inputAttr: {
					id: this.field.name,
					name: this.field.name,
					required: this.fieldRequired,
					readonly: this.field.readonly
				},
				onBlur: () => {
					this.formInputValueChangeHandler(this.inputValue);
				}
			});
		},
		activeValidation() {
			return this.isActiveValidation ? !this.initialized : false;
		},
		fieldRequired() {
			let hasRequireValidator = false;
			if ( this.field.validators ) {
				this.field.validators.forEach(validator => {
					if ( this.activeValidation && validator && validator.name === 'require' ) {
						hasRequireValidator = true;
					}
				});
			}
			return this.activeValidation && hasRequireValidator || this.field.required;
		},
		fieldIsValid() {
			return this.isActiveValidation ? [...this.fieldMessages, ...this.messages].length < 1 : true;
		},
		defaultValue() {
			return this.field.defaultValue !== undefined ? this.field.defaultValue : null;
		},
		isActiveValidation() {
			return this.field.activeValidation ?? false;
		},
		field() {
			return this.schema[this.fieldName];
		},
		fieldValidators() {
			return this.field.validators;
		},
		showLabel() {
			return (this.field.label !== null && this.field.inputType !== 'hidden' && this.field.type !== TYPE_FIELD.CHECKBOX);
		},
		inputValue: {
			set(value) {
				this.$emit('input', value || this.defaultValue);
				this.isFieldValid(this.field, value || this.defaultValue);
			},
			get() {
				return this.value;
			}
		}
	},

	watch: {
		value: {
			immediate: false,
			handler(v) {
				this.initialized = false;
				this.formInputValueChangeHandler(v);
			}
		},
		'schema': {
			immediate: false,
			deep: true,
			handler(v) {
				this.initialized = false;
				this.validate();
			}
		}
	}
};
</script>
